import { useEffect, useState } from 'react';
import { JSONObject } from '../../../../types/types';
import {
  FormVisibilityNode,
  getInitialFormItemVisibility,
} from '../utils/getInitialFormItemVisibility';
import { hasFormItemVisibilityExpression } from '../utils/hasFormItemVisibilityExpression';
import { resolveFormItemVisibility } from '../utils/resolveFormItemVisibility';

type ResolvedFormItemVisibility = {
  visible: boolean;
  pending: boolean;
};

export const useFormItemVisibility = (
  node: FormVisibilityNode,
  resolveData: JSONObject
): ResolvedFormItemVisibility => {
  const hasExpression = hasFormItemVisibilityExpression(node);
  const [isVisible, setIsVisible] = useState<boolean>(() =>
    getInitialFormItemVisibility(node)
  );
  const [isPending, setIsPending] = useState<boolean>(hasExpression);

  useEffect(() => {
    if (!hasExpression) {
      const initialVisibility = getInitialFormItemVisibility(node);
      setIsVisible((current) =>
        current === initialVisibility ? current : initialVisibility
      );
      setIsPending(false);
      return;
    }

    let isCancelled = false;
    setIsPending(true);
    resolveFormItemVisibility(node, resolveData)
      .then((resolvedVisibility: boolean) => {
        if (!isCancelled) {
          setIsVisible(resolvedVisibility);
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          console.error('[useFormItemVisibility]', error);
          setIsVisible(false);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsPending(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [hasExpression, node, resolveData]);

  return { visible: isVisible, pending: isPending };
};
